/**
 * Anyagok, szinek es minosegi profilok.
 *
 * A tartalom szandekosan egyezik az asztali alkalmazas katalogusaval
 * (VOXORA Forge, src/shared/catalog.ts), hogy a weboldalon adott
 * megrendeles egy az egyben atvihető legyen a gyartasba. Ha ott valtozik
 * egy profil, itt is at kell vezetni.
 */

export type MaterialId = 'PETG' | 'PLA'

export interface Material {
  id: MaterialId
  label: string
  /** g/cm³ - a filament profilbol. */
  density: number
  tagline: string
  strengths: string[]
  limits: string[]
}

export const MATERIALS: Material[] = [
  {
    id: 'PETG',
    label: 'PETG',
    density: 1.27,
    tagline: 'A biztos választás használati tárgyakhoz.',
    strengths: [
      'Jobban viseli az ütést és a mindennapi terhelést',
      'Melegebb környezetben is megbízhatóbb választás',
      'Kültéren és nedvesebb helyen is tartósabb',
      'Vízzárásra hangolt darabot ebből készítünk'
    ],
    limits: [
      'Az egészen apró részletek kevésbé élesek',
      'A támaszok helye könnyebben meglátszódhat a felületen'
    ]
  },
  {
    id: 'PLA',
    label: 'PLA',
    density: 1.24,
    tagline: 'Ha a részlet és a szép felület a fontos.',
    strengths: [
      'Figurákon és maketteken szépen mutatja az apró részleteket',
      'Sima, egyenletes felület érhető el vele',
      'Növényi alapú alapanyag'
    ],
    limits: [
      'Ridegebb: ütésnek vagy terhelésnek kitett alkatrészhez nem ezt javasoljuk',
      'Melegben meglágyulhat, napon álló autóba nem való'
    ]
  }
]

export interface ColorOption {
  id: string
  label: string
  material: MaterialId
  hex: string
}

export const COLORS: ColorOption[] = [
  { id: 'petg-black', label: 'Fekete', material: 'PETG', hex: '#17181B' },
  { id: 'petg-white', label: 'Fehér', material: 'PETG', hex: '#F2F1EC' },
  { id: 'petg-gold', label: 'Arany', material: 'PETG', hex: '#C8A54B' },
  { id: 'petg-blue', label: 'Kék', material: 'PETG', hex: '#2A5FA8' },
  { id: 'petg-pink', label: 'Strawberry Pink', material: 'PETG', hex: '#E4587A' },
  { id: 'petg-red', label: 'Piros', material: 'PETG', hex: '#BE2B2F' },
  { id: 'pla-black', label: 'Fekete', material: 'PLA', hex: '#17181B' },
  { id: 'pla-white', label: 'Fehér', material: 'PLA', hex: '#F2F1EC' }
]

/**
 * Minosegi profil.
 *
 * ────────────────────────────────────────────────────────────────────
 * AZ `id` A KOZOS NYELV
 *
 * Ez az azonosito kerul az adatbazisba, es EZT keresi meg a VOXORA Forge a
 * sajat katalogusaban (`src/shared/catalog.ts`), hogy megtalalja hozza az
 * OrcaSlicer presetet. Ha a ket lista azonositoi elternenek, a Forge
 * "Ismeretlen minőségi profil" hibaval elbukna MINDEN webes ajanlaton -
 * es a hiba csak a feldolgozasnal derulne ki, nem a rendelesfelvetelnel.
 * ────────────────────────────────────────────────────────────────────
 */
export interface QualityProfile {
  id: string
  label: string
  material: MaterialId
  layerHeight: number
  /**
   * `normal`  - a minosegi letra, csokkeno retegvastagsag szerint;
   * `special` - kulonleges celu profil (szilardsag, vizzarás).
   *
   * A ketto KULON csoportban jelenik meg. A kulonleges profilok nem
   * torhetik meg a minosegi sorrendet: a "Erős 0,25 mm" a Gyors (0,30) es
   * az Általános (0,20) koze esne, es a latogato azt hinne, hogy a lista
   * osszevissza all.
   */
  group: 'normal' | 'special'
  /** Kerulet-szam es vonalszelesseg a hejbecsleshez. */
  wallLoops: number
  lineWidth: number
  /** Kitoltes aranya (0-1). */
  infill: number
  summary: string
  bestFor: string
}

/*
  A SORREND KOTELEZO:

    Normal profilok CSOKKENO retegvastagsag szerint:
      Gyors 0,30 -> Általános 0,20 -> Részletes 0,12 -> Ultra részletes 0,08

    Kulonleges profilok kulon csoportban:
      Erős 0,25 -> Vízzárásra hangolt 0,20
*/
export const QUALITIES: QualityProfile[] = [
  {
    id: 'petg-gyors', label: 'Gyors', material: 'PETG', layerHeight: 0.3, group: 'normal',
    wallLoops: 2, lineWidth: 0.42, infill: 0.1,
    summary: 'Gyorsan elkészül, a rétegvonalak viszont láthatók maradnak.',
    bestFor: 'Méretpróba, illeszkedés ellenőrzése, egyszer használatos sablon'
  },
  {
    id: 'petg-altalanos', label: 'Általános', material: 'PETG', layerHeight: 0.2, group: 'normal',
    wallLoops: 3, lineWidth: 0.42, infill: 0.2,
    summary: 'Jó egyensúly a szép felület, a tartósság és az ár között.',
    bestFor: 'A legtöbb használati tárgyhoz. Ha bizonytalan vagy, ezt válaszd'
  },
  {
    id: 'petg-reszletes', label: 'Részletes', material: 'PETG', layerHeight: 0.12, group: 'normal',
    wallLoops: 3, lineWidth: 0.4, infill: 0.2,
    summary: 'Finomabb felület, a kisebb részletek is szépen kirajzolódnak.',
    bestFor: 'Szem előtt lévő tárgyak, feliratok és ívelt felületek'
  },
  {
    id: 'petg-ultra', label: 'Ultra részletes', material: 'PETG', layerHeight: 0.08, group: 'normal',
    wallLoops: 4, lineWidth: 0.4, infill: 0.2,
    summary: 'A legfinomabb beállításunk, érezhetően hosszabb nyomtatási idővel.',
    bestFor: 'Makett és apró dísztárgy, ahol minden részlet számít'
  },
  {
    id: 'petg-eros', label: 'Erős', material: 'PETG', layerHeight: 0.25, group: 'special',
    wallLoops: 4, lineWidth: 0.45, infill: 0.15,
    summary: 'Megerősített falak a nagyobb mindennapi terheléshez.',
    bestFor: 'Tartók, fogantyúk és más használati alkatrészek'
  },
  {
    id: 'petg-vizallo', label: 'Vízzárásra hangolt', material: 'PETG', layerHeight: 0.2, group: 'special',
    wallLoops: 5, lineWidth: 0.42, infill: 0.25,
    summary: 'Tömörebb falak a szivárgás kockázatának csökkentésére.',
    bestFor: 'Kaspó és cseppfogó tálca; nyomás alatti vízhez nem ajánlott'
  },
  {
    id: 'pla-gyors', label: 'Gyors', material: 'PLA', layerHeight: 0.3, group: 'normal',
    wallLoops: 2, lineWidth: 0.42, infill: 0.1,
    summary: 'Gyorsan elkészül, a rétegvonalak láthatók maradnak.',
    bestFor: 'Méretellenőrzés és első próbadarab'
  },
  {
    id: 'pla-altalanos', label: 'Általános', material: 'PLA', layerHeight: 0.2, group: 'normal',
    wallLoops: 3, lineWidth: 0.42, infill: 0.2,
    summary: 'Jó egyensúly a részletek, a nyomtatási idő és az ár között.',
    bestFor: 'Dekoráció, játék és általános próbadarab'
  },
  {
    id: 'pla-reszletes', label: 'Részletes', material: 'PLA', layerHeight: 0.12, group: 'normal',
    wallLoops: 3, lineWidth: 0.4, infill: 0.2,
    summary: 'Finom részletek és szebb felület, valamivel hosszabb idővel.',
    bestFor: 'Figurák és kisebb, szem előtt lévő modellek'
  },
  {
    id: 'pla-ultra', label: 'Ultra részletes', material: 'PLA', layerHeight: 0.08, group: 'normal',
    wallLoops: 4, lineWidth: 0.4, infill: 0.2,
    summary: 'A legtöbb kihozható részlet, hosszabb nyomtatási idővel.',
    bestFor: 'Miniatűrök, arcvonások és finom minták'
  }
]

/** A gep epitesi tere. A tipust szandekosan nem nevezzuk meg sehol. */
export const BUILD_VOLUME = { x: 260, y: 260, z: 260 }

/**
 * A LEGTOBB darab, ami egy modellbol rendelheto.
 *
 * ────────────────────────────────────────────────────────────────────
 * EZ A SZAM NEGY HELYEN EL, ES EGYUTT KELL MOZOGNIUK.
 *
 *   itt (az urlap felso hatara),
 *   a `quote_items_quantity_check` es a `legacy_orders` ellenorzes,
 *   a `quote_request_create` RPC vagasa,
 *   es a Forge `shared/order.ts` `MAX_ITEM_QUANTITY`-je.
 *
 * Ha barmelyik lemarad, a vevo egy olyan darabszamot ad meg, amit a lanc
 * kovetkezo eleme elutasit vagy csendben levag - es a hiba csak az
 * ajanlaton latszik meg.
 *
 * A korlat 99 volt; 2026-08-28-tol 1000, mert 100 darabos rendeles is
 * erkezett, es a regi hatar egyszeruen nem engedte leadni.
 * ────────────────────────────────────────────────────────────────────
 */
export const MAX_ITEM_QUANTITY = 1000

// ---------------------------------------------------------------- atvetel

/**
 * Az azonositok az adatbazis `delivery_kind` enumjaval EGYEZNEK.
 * A futarszolgalat neve nem resze az azonositonak: ha holnap masik
 * szolgaltatohoz megyunk, a mar felvett rendelesek adata nem valik
 * ertelmetlenne.
 */
export type DeliveryId = 'parcel_point' | 'home' | 'personal'

export interface DeliveryOption {
  id: DeliveryId
  label: string
  blurb: string
  detail: string
}

export const DELIVERY_OPTIONS: DeliveryOption[] = [
  {
    id: 'parcel_point',
    label: 'Csomagpont',
    blurb: 'Automata vagy átvevőpont, amikor neked jó.',
    detail:
      'Foxpost, Packeta, MPL, GLS vagy DPD pont. A visszaigazolásban küldjük a ' +
      'pontos címet és a nyitvatartást. A csomag általában 5 munkanapig vár rád.'
  },
  {
    id: 'home',
    label: 'Házhoz',
    blurb: 'A futár becsönget. Neked annyi a dolgod, hogy ajtót nyiss.',
    detail:
      'Futárszolgálat viszi a megadott címre, munkanapokon. Előtte SMS-ben vagy ' +
      'e-mailben jelez, hogy mikor érkezik.'
  },
  {
    id: 'personal',
    label: 'Személyesen',
    blurb: 'Átvétel Budapest I. kerületében.',
    detail:
      'A személyes átvétel díjmentes. A pontos helyet és az időpontot a ' +
      'visszaigazolás után e-mailben egyeztetjük.'
  }
]

// ---------------------------------------------------------------- fizetes

/**
 * ────────────────────────────────────────────────────────────────────
 * EGYEDI GYARTAS = ELORE UTALAS. UTANVET NINCS.
 *
 * Nem kenyelmi dontés, es nem bizalmatlansag. Egy egyedi darab kizarolag
 * a te megrendelesedre keszul: ha nem veszed at, masnak nem eladhato -
 * kidobjuk. Az at nem vett darabok koltseget vagy mindenki arába
 * epitenenk be, vagy elore utalast kerunk. A masodikat valasztottuk, mert
 * az olcsobb annak, aki tenyleg at is veszi a csomagjat.
 *
 * A regi utanvetes rendelesek adata megmarad (`legacy_orders`), de UJ
 * utanvetes rendeles sem itt, sem a szerveren nem jöhet letre: az uj sema
 * `payment_kind` enumjanak egyetlen erteke van.
 * ────────────────────────────────────────────────────────────────────
 */
export type PaymentId = 'bank_transfer'

export const PAYMENT_EXPLANATION =
  'Egyedi gyártásnál a termék kifejezetten a te megrendelésedre készül, ezért a ' +
  'gyártást az előre utalás beérkezése után kezdjük. Így elkerülhető, hogy az át ' +
  'nem vett, másnak nem értékesíthető darabok költsége beépüljön mindenki árába.'

export interface PaymentOption {
  id: PaymentId
  label: string
  blurb: string
}

export const PAYMENT_OPTIONS: PaymentOption[] = [
  {
    id: 'bank_transfer',
    label: 'Banki átutalás',
    blurb: 'Az ajánlat elfogadása után megkapod az utalási adatokat és a közleményt.'
  }
]

export const materialById = (id: MaterialId): Material =>
  MATERIALS.find((m) => m.id === id)!

export const colorsFor = (material: MaterialId): ColorOption[] =>
  COLORS.filter((c) => c.material === material)

/**
 * CSOKKENO RETEGVASTAGSAG.
 *
 * ────────────────────────────────────────────────────────────────────
 * A RENDEZES ITT TORTENIK, NEM A TOMB SORRENDJEBEN.
 *
 * A `QUALITIES` tomb ma jo sorrendben all, de erre NEM tamaszkodunk: eleg
 * egy uj profilt a lista vegere irni, es a valaszto sorrendje csendben
 * elromlana - a hiba pedig csak a kesz oldalon latszana.
 *
 * A rendezes MASOLATON tortenik (`[...]`), mert a `sort` helyben rendez, es
 * a modul szintu `QUALITIES` tomb atrendezese minden mas hivot is
 * megvaltoztatna.
 * ────────────────────────────────────────────────────────────────────
 */
export const byLayerHeightDesc = (list: QualityProfile[]): QualityProfile[] =>
  [...list].sort((a, b) => b.layerHeight - a.layerHeight)

export const qualitiesFor = (material: MaterialId): QualityProfile[] =>
  byLayerHeightDesc(QUALITIES.filter((q) => q.material === material))

/**
 * A ket csoport kulon, mindkettoben csokkeno retegvastagsaggal.
 *
 *   Minőség:          Gyors 0,30 -> Általános 0,20 -> Részletes 0,12 -> Ultra 0,08
 *   Különleges célra: Erős 0,25 -> Vízzárásra hangolt 0,20
 *
 * A kulonleges profilok azert allnak kulon csoportban, mert kulonben az
 * "Erős" (0,25 mm) a Gyors es az Általános koze esne, es a minosegi letra
 * ertelmet vesztene.
 */
export const qualityGroups = (
  material: MaterialId
): Array<{ title: string; items: QualityProfile[] }> => {
  const all = qualitiesFor(material)
  const groups = [
    { title: 'Minőség', items: byLayerHeightDesc(all.filter((q) => q.group === 'normal')) },
    { title: 'Különleges célra', items: byLayerHeightDesc(all.filter((q) => q.group === 'special')) }
  ]
  return groups.filter((g) => g.items.length > 0)
}

export const qualityById = (id: string): QualityProfile | undefined =>
  QUALITIES.find((q) => q.id === id)

export const colorById = (id: string): ColorOption | undefined =>
  COLORS.find((c) => c.id === id)
